import React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";

const ServicePageWrapper = styled.div`
  padding-top: 120px;
  min-height: 100vh;
  background: #fff;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 80px;
`;

const PageTitle = styled.h1`
  font-size: 3rem;
  color: var(--text-dark);
  margin-bottom: 20px;
  font-family: "Phudu", sans-serif;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const PageSubtitle = styled.p`
  font-size: 1.2rem;
  color: var(--text-light);
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  margin-bottom: 80px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const ContentSection = styled.div`
  h2 {
    font-size: 2rem;
    color: var(--text-dark);
    margin-bottom: 20px;
    font-family: "Phudu", sans-serif;
  }

  p {
    color: var(--text-light);
    line-height: 1.6;
    margin-bottom: 20px;
  }

  ul {
    list-style: none;
    padding: 0;

    li {
      color: var(--text-light);
      padding: 10px 0;
      border-bottom: 1px solid #f0f0f0;

      &:before {
        content: "✓";
        color: var(--secondary-color);
        font-weight: bold;
        margin-right: 10px;
      }
    }
  }
`;

const ImageSection = styled.div`
  img {
    width: 100%;
    height: 400px;
    object-fit: cover;
    border-radius: 20px;
  }
`;

const SolutionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  margin: 60px 0;
`;

const SolutionCard = styled.div`
  background: #f8f9fa;
  padding: 30px;
  border-radius: 15px;

  .icon {
    font-size: 3rem;
    color: var(--secondary-color);
    margin-bottom: 20px;
  }

  h3 {
    font-size: 1.5rem;
    color: var(--text-dark);
    margin-bottom: 15px;
    font-family: "Phudu", sans-serif;
  }

  p {
    color: var(--text-light);
    margin-bottom: 20px;
    font-size: 0.95rem;
  }

  .applications {
    list-style: none;
    padding: 0;

    li {
      color: var(--text-light);
      padding: 3px 0;
      font-size: 0.85rem;

      &:before {
        content: "•";
        color: var(--secondary-color);
        margin-right: 8px;
      }
    }
  }
`;

const IndustryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin: 60px 0;
`;

const IndustryCard = styled.div`
  background: white;
  border: 2px solid #f0f0f0;
  padding: 25px;
  border-radius: 10px;
  text-align: center;
  transition: all 0.3s ease;

  &:hover {
    border-color: var(--secondary-color);
    transform: translateY(-5px);
  }

  .icon {
    font-size: 2.5rem;
    color: var(--secondary-color);
    margin-bottom: 15px;
  }

  h4 {
    color: var(--text-dark);
    margin-bottom: 10px;
    font-family: "Phudu", sans-serif;
  }

  p {
    color: var(--text-light);
    font-size: 0.9rem;
    margin: 0;
  }
`;

const CTASection = styled.div`
  background: var(--background-dark);
  color: white;
  padding: 60px 40px;
  border-radius: 20px;
  text-align: center;
  margin-bottom: 80px;

  h2 {
    font-size: 2rem;
    margin-bottom: 20px;
    font-family: "Phudu", sans-serif;
  }

  p {
    margin-bottom: 30px;
    opacity: 0.9;
  }

  .btn {
    background: var(--secondary-color);
    color: white;
    padding: 15px 30px;
    border-radius: 10px;
    text-decoration: none;
    font-weight: 600;
    display: inline-block;
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 10px 20px rgba(245, 115, 58, 0.3);
    }
  }
`;

const ContainerStorageSolutions = () => {
  return (
    <ServicePageWrapper>
      <Container>
        <PageHeader>
          <PageTitle>Container Storage Solutions</PageTitle>
          <PageSubtitle>
            Comprehensive storage solutions for businesses, construction sites,
            and residential needs. Secure, weather-resistant, and adaptable to
            any environment.
          </PageSubtitle>
        </PageHeader>

        <ContentGrid>
          <ContentSection>
            <h2>Versatile Storage Solutions</h2>
            <p>
              Our container storage solutions provide secure, weather-resistant
              storage for virtually any application. From temporary project
              storage to permanent warehouse expansion, our containers offer
              unmatched versatility and security.
            </p>
            <p>
              Available in multiple sizes and configurations, with options for
              climate control, shelving systems, and custom modifications to
              meet your specific storage requirements.
            </p>
            <ul>
              <li>Secure, lockable storage</li>
              <li>Weather-resistant and waterproof</li>
              <li>Multiple size options available</li>
              <li>Ground-level access</li>
              <li>Stackable for maximum space efficiency</li>
              <li>Custom shelving and organization systems</li>
            </ul>
          </ContentSection>

          <ImageSection>
            <img
              src="/assets/2025/08/storage-containers.jpg"
              alt="Storage Solutions"
            />
          </ImageSection>
        </ContentGrid>

        <h2
          style={{
            textAlign: "center",
            marginBottom: "40px",
            fontFamily: "Phudu, sans-serif",
            color: "var(--text-dark)",
          }}
        >
          Storage Solution Types
        </h2>

        <SolutionGrid>
          <SolutionCard>
            <div className="icon">
              <i className="fas fa-warehouse"></i>
            </div>
            <h3>Business Storage</h3>
            <p>
              Expand your storage capacity with secure container solutions.
              Perfect for inventory overflow, seasonal items, and document
              storage.
            </p>
            <ul className="applications">
              <li>Inventory and merchandise</li>
              <li>Seasonal equipment</li>
              <li>Document archives</li>
              <li>Equipment and tools</li>
            </ul>
          </SolutionCard>

          <SolutionCard>
            <div className="icon">
              <i className="fas fa-hard-hat"></i>
            </div>
            <h3>Construction Storage</h3>
            <p>
              Secure on-site storage for tools, materials, and equipment.
              Weather-resistant protection for valuable construction assets.
            </p>
            <ul className="applications">
              <li>Construction tools and equipment</li>
              <li>Building materials</li>
              <li>Safety equipment</li>
              <li>Project documentation</li>
            </ul>
          </SolutionCard>

          <SolutionCard>
            <div className="icon">
              <i className="fas fa-home"></i>
            </div>
            <h3>Residential Storage</h3>
            <p>
              Personal storage solutions for home renovations, moving, or
              long-term storage of household items and recreational equipment.
            </p>
            <ul className="applications">
              <li>Household goods during moves</li>
              <li>Seasonal decorations</li>
              <li>Recreational vehicles and equipment</li>
              <li>Home renovation overflow</li>
            </ul>
          </SolutionCard>

          <SolutionCard>
            <div className="icon">
              <i className="fas fa-calendar-week"></i>
            </div>
            <h3>Event Storage</h3>
            <p>
              Temporary storage solutions for events, festivals, and seasonal
              businesses. Secure storage that moves with your event schedule.
            </p>
            <ul className="applications">
              <li>Event equipment and supplies</li>
              <li>Festival merchandise</li>
              <li>Catering equipment</li>
              <li>Temporary retail inventory</li>
            </ul>
          </SolutionCard>
        </SolutionGrid>

        <h2
          style={{
            textAlign: "center",
            marginBottom: "40px",
            fontFamily: "Phudu, sans-serif",
            color: "var(--text-dark)",
          }}
        >
          Industries We Serve
        </h2>

        <IndustryGrid>
          <IndustryCard>
            <div className="icon">
              <i className="fas fa-industry"></i>
            </div>
            <h4>Manufacturing</h4>
            <p>Equipment and material storage</p>
          </IndustryCard>

          <IndustryCard>
            <div className="icon">
              <i className="fas fa-store"></i>
            </div>
            <h4>Retail</h4>
            <p>Inventory and seasonal storage</p>
          </IndustryCard>

          <IndustryCard>
            <div className="icon">
              <i className="fas fa-seedling"></i>
            </div>
            <h4>Agriculture</h4>
            <p>Equipment and harvest storage</p>
          </IndustryCard>

          <IndustryCard>
            <div className="icon">
              <i className="fas fa-graduation-cap"></i>
            </div>
            <h4>Education</h4>
            <p>Supplies and equipment storage</p>
          </IndustryCard>

          <IndustryCard>
            <div className="icon">
              <i className="fas fa-truck"></i>
            </div>
            <h4>Logistics</h4>
            <p>Temporary and overflow storage</p>
          </IndustryCard>

          <IndustryCard>
            <div className="icon">
              <i className="fas fa-hammer"></i>
            </div>
            <h4>Construction</h4>
            <p>Tools and material security</p>
          </IndustryCard>
        </IndustryGrid>

        <CTASection>
          <h2>Find Your Perfect Storage Solution</h2>
          <p>
            Contact our storage specialists to design a custom solution for your
            specific needs. We offer flexible rental and purchase options.
          </p>
          <Link to="/contact" className="btn">
            Design My Solution
          </Link>
        </CTASection>
      </Container>
    </ServicePageWrapper>
  );
};

export default ContainerStorageSolutions;
